<template>
  <div class="p-6">
    <div v-if="!checking_subs">
      <div v-if="authorized" class="flex md:flex-row flex-col">
        <!-- Subscription Details -->
        <div class="flex-1 px-2">
          <div class="border rounded p-4">
            <p class="font-semibold text-xl border-b py-2 mb-4">
              <i class="fa fa-shopping-cart text-brand"></i>
              Subscription Details
            </p>

            <div class="flex flex-col md:flex-row">
              <div class="flex-1 p-2">
                <p class="text-lg font-semibold pb-4">Current Plan</p>
                <div class="border flex-1" v-if="subscription_details">
                  <div
                    class="
                      py-4
                      text-center
                      bg-brand-lightest
                      uppercase
                      text-lg
                      tracking-wider
                      text-grey-darker
                    "
                  >
                    {{ subscription_details.plan.name }}
                  </div>
                  <div
                    class="
                      py-4
                      text-center
                      bg-modal-gradient
                      border-t
                      flex flex-col
                    "
                  >
                    <div
                      class="w-full flex text-white justify-center items-center"
                    >
                      <span
                        class="font-light text-3xl text-right currency -mt-3"
                        >$</span
                      >
                      <span class="font-light text-6xl amount">{{
                        parseFloat(subscription_details.plan.price).toFixed(2)
                      }}</span>
                    </div>
                  </div>
                  <div
                    class="
                      p-4
                      text-center
                      bg-grey-lightest
                      text-lg text-grey-darker
                    "
                  >
                    Charged
                    <span class="font-semibold capitalize">{{
                      subscription_details.plan.interval
                    }}</span>
                  </div>
                  <div
                    class="
                      p-4
                      bg-white
                      text-sm
                      uppercase
                      text-grey-darker text-center
                    "
                  >
                    <router-link
                      :to="{
                        name: 'change-plan',
                        params: { vault_id: $route.params.vault_id },
                      }"
                      class="
                        px-6
                        py-3
                        text-white
                        bg-brand
                        no-underline
                        rounded
                        mb-4
                        hover:bg-brand-dark
                        font-medium
                        block
                      "
                    >
                      Change Plan
                    </router-link>
                    <router-link
                      :to="{
                        name: 'update-billing-details',
                        params: { vault_id: $route.params.vault_id },
                      }"
                      class="
                        px-6
                        py-3
                        text-brand
                        border border-brand
                        no-underline
                        rounded
                        hover:bg-brand hover:text-white
                        font-medium
                        block
                      "
                      >Update Payment Details</router-link
                    >
                  </div>
                </div>
              </div>

              <div class="flex-1 p-2">
                <p class="text-lg font-semibold pb-4">Other Details</p>

                <p class="font-semibold mb-2 text-grey-dark">
                  Subscription Start
                </p>
                <p class="mb-4">
                  {{ formatDate(subscription_details.start_on) }}
                </p>

                <p class="font-semibold mb-2 text-grey-dark">Next Billing</p>
                <div class="mb-4" v-if="subscription_details">
                  <p v-if="subscription_details.plan.interval == 'monthly'">
                    Billed every
                    <span class="font-semibold">{{
                      getNextMonth(subscription_details.bill_on)
                    }}</span>
                    day of the month
                  </p>

                  <p v-else>{{ getNextYear(subscription_details.start_on) }}</p>
                </div>

                <p class="text-lg font-semibold pb-4 mt-8 border-t pt-2">
                  Discounts
                </p>

                <div
                  class="bg-orange-lightest text-orange-dark p-4 text-center"
                  role="alert"
                  v-if="!subscription_details.discount_is_enabled"
                >
                  <p>Your Subscription is not discounted</p>
                </div>

                <div class="mb-4" v-else>
                  <p
                    v-if="subscription_details.discount_is_percent"
                    class="mb-2"
                  >
                    {{ subscription_details.discount_amount }}
                    % Discount on this Subscription
                  </p>

                  <p v-else class="mb-2">
                    {{ subscription_details.discount_amount }} Off on this
                    Subscription
                  </p>

                  <p class="mb-2">
                    Discount will start at
                    <span class="font-semibold">{{
                      formatDate(subscription_details.discount_start_at)
                    }}</span>
                    and ends at
                    <span class="font-semibold">{{
                      formatDate(subscription_details.discount_end_at)
                    }}</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Latest Payment and Invoice -->
        <div class="flex-1 px-2 flex flex-col my-4 md:my-0">
          <div class="border rounded p-4 mb-4">
            <p class="font-semibold text-xl border-b py-2 mb-4">
              <i class="fa fa-file-o text-brand"></i>
              Latest Invoice
            </p>

            <div
              v-if="
                subscription_details &&
                subscription_details.invoices.length != 0
              "
            >
              <div class="border rounded border-grey-light p-4 flex">
                <div class="flex-1">
                  <p class="py-2">
                    <span class="font-semibold mr-2">Invoice ID:</span>
                    #{{ subscription_details.invoices[0].id }}
                  </p>
                  <p class="py-2">
                    <span class="font-semibold mr-2">Amount:</span>
                    ${{ subscription_details.invoices[0].amount_due }}
                  </p>
                  <p class="py-2">
                    <span class="font-semibold mr-2">Billed On:</span>
                    {{ formatDate(subscription_details.invoices[0].for_date) }}
                  </p>
                  <p class="py-2">
                    <span class="font-semibold mr-2">Paid:</span>
                    <span v-if="subscription_details.invoices[0].payments.length == 0">
                      No
                    </span>
                    <span v-else-if="subscription_details.invoices[0].payments[0]
                        .status_code == 'C' || subscription_details.invoices[0].payments[0]
                        .status_code == 'S'">Yes</span>
                      <span v-else>
                      No - {{subscription_details.invoices[0].payments[0].error_message }}
                    </span>
                  </p>
                </div>

                <div class="flex-1 flex justify-center">
                  <button
                    @click.prevent="
                      downloadInvoice(subscription_details.invoices[0].id)
                    "
                    class="
                      p-3
                      text-brand
                      border border-brand
                      no-underline
                      rounded
                      hover:bg-brand hover:text-white
                      font-medium
                      block
                      self-center
                    "
                    :class="{ 'cursor-not-allowed opacity-75': downloading }"
                    :disabled="downloading"
                  >
                    <span v-if="!downloading">
                      <i class="fa fa-download"></i> Download Invoice
                    </span>
                    <span v-else>Please wait...</span>
                  </button>
                </div>
              </div>

              <div class="mt-4 flex justify-end">
                <router-link
                  :to="{
                    name: 'invoices',
                    params: { vault_id: $route.params.vault_id },
                  }"
                  class="
                    border border-brand
                    p-2
                    no-underline
                    text-brand
                    rounded
                  "
                >
                  View All Invoices
                </router-link>
              </div>
            </div>

            <div
              class="bg-orange-lightest text-orange-dark p-4"
              role="alert"
              v-else
            >
              <p>No available Invoice</p>
            </div>
          </div>

          <div class="border rounded p-4">
            <p class="font-semibold text-xl border-b py-2 mb-4">
              <i class="fa fa-dollar text-brand"></i>
              Latest Payment
            </p>
            <div
              v-if="
                subscription_details &&
                subscription_details.invoices.length != 0
              "
            >
              <div
                class="bg-orange-lightest text-orange-dark p-4"
                role="alert"
                v-if="subscription_details.invoices[0].payments.length == 0"
              >
                <p>Current Invoice is not yet paid</p>
              </div>

              <div v-else>
                <div class="border rounded border-grey-light p-4">
                  <p class="py-2">
                    <span class="font-semibold mr-2">Payment ID:</span>
                    #{{ subscription_details.invoices[0].payments[0].id }}
                  </p>
                  <p class="py-2">
                    <span class="font-semibold mr-2">Transaction Amount:</span>
                    ${{
                      subscription_details.invoices[0].payments[0]
                        .amount
                    }}
                  </p>
                  <p class="py-2">
                    <span class="font-semibold mr-2">Payment Message:</span>
                    {{
                      subscription_details.invoices[0].payments[0]
                        .description
                    }}
                  </p>
                  <p class="py-2">
                    <span class="font-semibold mr-2">Date Settled:</span>
                    {{
                      formatDate(
                        subscription_details.invoices[0].payments[0]
                          .payment_settlement_date
                      )
                    }}
                  </p>
                </div>
              </div>
            </div>
            <div
              class="bg-orange-lightest text-orange-dark p-4"
              role="alert"
              v-else
            >
              <p>No available payments</p>
            </div>
          </div>
        </div>
      </div>

      <div
        class="bg-orange-lightest text-orange-dark p-4 text-center w-full"
        role="alert"
        v-else
      >
        <p>
          Unable to view subscription details because you do not have any
          subscriptions or you are unauthorized.
        </p>
      </div>
    </div>

    <div
      v-else
      class="w-full h-screen flex flex-col justify-center items-center"
    >
      <i class="fa fa-circle-notch fa-spin fa-3x"></i>
      <p class="mt-4">Please wait...</p>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      subscription_details: "",
      current_subscription: "",
      authorized: false,
      checking_subs: true,
      downloading: false,
    };
  },

  mounted() {
    this.getCurrentSubscription();
  },

  methods: {
    getCurrentSubscription() {
      axios.get("api/users/subscriptions").then(({ data }) => {
        this.checking_subs = false;
        let vault_id = this.$route.params.vault_id;

        this.current_subscription = _.find(data, function (subscription) {
          if(subscription.vault && subscription.vault.id) {
            return subscription.vault.id == vault_id;
          }
        });

        if (!_.isEmpty(this.current_subscription)) {
          this.authorized = true;
          this.getSubscriptionDetails();
        }
      });
    },

    getSubscriptionDetails() {
      axios
        .get("api/users/subscriptions/" + this.current_subscription.id)
        .then(({ data }) => {
          this.subscription_details = data;
        });
    },

    getNextYear(date) {
      return moment(date).add(1, "y").format("dddd, MMMM Do YYYY");
    },

    getNextMonth(day) {
      return moment().date(day).format("Do");
    },

    formatDate(date) {
      return moment(date).format("dddd, MMMM Do YYYY");
    },

    downloadInvoice(invoice_id) {
      this.downloading = true;
      axios
        .get(
          `api/users/subscriptions/${this.current_subscription.id}/invoices/${invoice_id}/download`,
          {
            responseType: "blob",
          }
        )
        .then(({ data }) => {
          this.downloading = false;
          const url = window.URL.createObjectURL(new Blob([data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `Invoice # ${invoice_id}.pdf`); //or any other extension
          document.body.appendChild(link);
          link.click();
        });
    },
  },
};
</script>

<style>
</style>